<template>
    <slot />
    <v-dialog v-model="dialog" width="auto" :min-width="mobile ? undefined : 450" min-height="200" data-id="errorDialog">
        <v-card rounded="lg">
            <v-card-text class="pb-0">
                <div class="d-flex align-center mb-6">
                    <v-icon icon="mdi-alert-circle" color="error" size="32" />
                    <div class="text-h2 ml-3">{{ t('$vuetify.errorDialog.title', 'System error') }}</div>
                </div>

                <div class="text-h6">
                    {{ errorMessage }}
                </div>
            </v-card-text>
            <v-card-actions class="d-flex justify-end pt-6 pb-4 px-6">
                <v-btn variant="text" :color="showRefreshButton ? undefined : 'primary'" @click="dialog = false">
                    {{ t('$vuetify.errorDialog.close', 'Close') }}
                </v-btn>
                <v-btn v-if="showRefreshButton" color="primary" variant="flat" @click="refresh">
                    {{ t('$vuetify.errorDialog.reload', 'Reload') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { computed, onErrorCaptured, onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';

interface Props {
    stopPropagation?: boolean;
    /** Array of known error types that will display non-generic error messages. */
    errorsToDisplay: string[];
}

interface Emits {
    (e: 'errorCaptured', err?: Error): void;
    (e: 'reload'): void;
    (e: 'dialogClosed'): void;
}

const props = defineProps<Props>();
const lastKnownError = defineModel<Error | null>();
const emit = defineEmits<Emits>();

const { t, te } = useI18n();
const dialog = ref(false);
const { mobile } = useDisplay();

const showRefreshButton = computed(
    () => !lastKnownError.value || (lastKnownError.value && !props.errorsToDisplay.includes(lastKnownError.value.name))
);
const errorMessage = computed(() => {
    if (lastKnownError?.value && props.errorsToDisplay.includes(lastKnownError.value.name)) {
        // return translation if it exists
        return te(lastKnownError.value.message) ? t(lastKnownError.value.message) : lastKnownError.value.message;
    }
    // default error
    return t('$vuetify.errorDialog.subtitle', 'An unexpected error has occurred, please refresh window to proceed');
});

onErrorCaptured(err => {
    // you can also  get `component`, `info` if needed - just add to the function argument list
    emit('errorCaptured', err);
    return !props.stopPropagation;
});

onMounted(() => {
    watch(lastKnownError, (value, oldValue) => {
        if (value !== null && value !== oldValue) {
            console.error(value);
            dialog.value = true;
        }
    });
});

function refresh() {
    emit('reload');
}

watch(dialog, value => {
    if (!value) {
        // wait for animation to complete before resetting value
        setTimeout(() => {
            lastKnownError.value = null;
            emit('dialogClosed');
        }, 200);
    }
});
</script>
